import { useEffect, useCallback, useState, useRef } from 'react';

/**
 * Hook to enforce fullscreen mode on TV/Kiosk devices.
 * Automatically requests fullscreen and re-enters if user exits.
 *
 * Usage:
 *   const { isFullscreen, requestFullscreen, exitFullscreen } = useFullscreen({
 *     enabled: true,
 *     autoEnter: true,
 *     autoReenter: true
 *   });
 */
export function useFullscreen(options = {}) {
  const {
    enabled = true,
    autoEnter = true,
    autoReenter = true,
    reenterDelayMs = 2000,
    targetElement = null, // null means document.documentElement
  } = options;

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSupported, setIsSupported] = useState(true);

  // MEMORY FIX: Exponential backoff for fullscreen retries
  const fullscreenRetryDelayRef = useRef(reenterDelayMs); // Start at reenterDelayMs (default 2s)
  const fullscreenAttemptCountRef = useRef(0);
  const fullscreenRetryTimerRef = useRef(null);
  const MAX_FULLSCREEN_ATTEMPTS = 5;
  const MAX_FULLSCREEN_RETRY_DELAY = 30000; // 30s cap

  // Get the target element for fullscreen
  const getTarget = useCallback(() => {
    return targetElement || document.documentElement;
  }, [targetElement]);

  // Request fullscreen with cross-browser support
  const requestFullscreen = useCallback(async () => {
    if (!enabled) return false;

    const target = getTarget();

    try {
      if (target.requestFullscreen) {
        await target.requestFullscreen();
      } else if (target.webkitRequestFullscreen) {
        // Safari/older Chrome
        await target.webkitRequestFullscreen();
      } else if (target.mozRequestFullScreen) {
        // Firefox
        await target.mozRequestFullScreen();
      } else if (target.msRequestFullscreen) {
        // IE/Edge
        await target.msRequestFullscreen();
      } else if (target.webkitEnterFullscreen) {
        // iOS Safari video elements
        await target.webkitEnterFullscreen();
      } else {
        console.warn('[Fullscreen] API not supported');
        setIsSupported(false);
        return false;
      }
      return true;
    } catch (err) {
      // Fullscreen request may fail if:
      // - Not triggered by user gesture
      // - Browser policy prevents it
      // - Already in fullscreen
      console.warn('[Fullscreen] Request failed:', err.message);
      return false;
    }
  }, [enabled, getTarget]);

  // Exit fullscreen
  const exitFullscreen = useCallback(async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
      return true;
    } catch (err) {
      console.warn('[Fullscreen] Exit failed:', err.message);
      return false;
    }
  }, []);

  // Check if currently in fullscreen
  const checkFullscreen = useCallback(() => {
    return !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    );
  }, []);

  // Handle fullscreen change events
  useEffect(() => {
    if (!enabled) return;

    const handleFullscreenChange = () => {
      const nowFullscreen = checkFullscreen();
      setIsFullscreen(nowFullscreen);

      if (nowFullscreen) {
        // MEMORY FIX: Reset backoff on successful fullscreen entry
        fullscreenRetryDelayRef.current = reenterDelayMs;
        fullscreenAttemptCountRef.current = 0;
        console.log('[Fullscreen] State changed: fullscreen entered');
      } else {
        console.log('[Fullscreen] State changed: exited fullscreen');
      }

      // Auto re-enter fullscreen if user exits (for kiosk mode)
      // MEMORY FIX: Exponential backoff with max attempts
      if (!nowFullscreen && autoReenter && fullscreenAttemptCountRef.current < MAX_FULLSCREEN_ATTEMPTS) {
        const delay = fullscreenRetryDelayRef.current;
        if (fullscreenAttemptCountRef.current === 0) {
          console.warn('[Fullscreen] Exited - will attempt re-entry in', delay, 'ms');
        } else {
          console.debug('[Fullscreen] Retry attempt', fullscreenAttemptCountRef.current + 1, 'in', delay, 'ms');
        }
        // Clear any existing retry timer
        if (fullscreenRetryTimerRef.current) {
          clearTimeout(fullscreenRetryTimerRef.current);
        }
        fullscreenRetryTimerRef.current = setTimeout(() => {
          if (!checkFullscreen()) {
            fullscreenAttemptCountRef.current++;
            // Double the delay for next retry, cap at MAX
            fullscreenRetryDelayRef.current = Math.min(delay * 2, MAX_FULLSCREEN_RETRY_DELAY);
            requestFullscreen();
          }
        }, delay);
      } else if (!nowFullscreen && fullscreenAttemptCountRef.current >= MAX_FULLSCREEN_ATTEMPTS) {
        console.warn('[Fullscreen] Max retry attempts reached, stopping auto-reentry');
      }
    };

    // MEMORY FIX: Reset retry counter on user interaction (tap-to-fullscreen)
    const handleUserInteraction = () => {
      fullscreenRetryDelayRef.current = reenterDelayMs;
      fullscreenAttemptCountRef.current = 0;
    };

    // Listen for fullscreen changes (cross-browser)
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);

    // Set initial state
    setIsFullscreen(checkFullscreen());

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
      // Clean up any pending retry timer
      if (fullscreenRetryTimerRef.current) {
        clearTimeout(fullscreenRetryTimerRef.current);
        fullscreenRetryTimerRef.current = null;
      }
    };
  }, [enabled, autoReenter, reenterDelayMs, checkFullscreen, requestFullscreen]);

  // Auto-enter fullscreen on mount (if enabled)
  useEffect(() => {
    if (!enabled || !autoEnter) return;

    // Only auto-enter fullscreen on CTV/kiosk devices — desktop browsers reject it without user gesture
    const ua = navigator.userAgent || '';
    const isCtvOrKiosk = /AFT[A-Z]|Fire TV|CrKey|Chromecast|Tizen|webOS|NetCast|SMART-TV|Android.*wv\)/i.test(ua)
      || Boolean(window.__TRILL_IS_CTV__);
    if (!isCtvOrKiosk) return;

    // Delay slightly to ensure component is mounted
    const timer = setTimeout(() => {
      if (!checkFullscreen()) {
        console.log('[Fullscreen] Auto-entering fullscreen mode');
        requestFullscreen();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [enabled, autoEnter, checkFullscreen, requestFullscreen]);

  // Hide cursor for TV mode
  useEffect(() => {
    if (!enabled) return;

    // Add style to hide cursor
    const style = document.createElement('style');
    style.id = 'trillboards-fullscreen-style';
    style.textContent = `
      /* Hide cursor for TV kiosk mode */
      html, body, * {
        cursor: none !important;
      }
      /* Ensure no scrollbars */
      html, body {
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
      }
    `;

    // Only add if not already present
    if (!document.getElementById('trillboards-fullscreen-style')) {
      document.head.appendChild(style);
    }

    return () => {
      const existingStyle = document.getElementById('trillboards-fullscreen-style');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [enabled]);

  return {
    isFullscreen,
    isSupported,
    requestFullscreen,
    exitFullscreen,
  };
}

export default useFullscreen;
